// src/pages/oauth/AdminClients.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../../utils/media';
import Layout from '../../components/Layout';

const joinUrl = (base, path) =>
  `${String(base).replace(/\/+$/, '')}/${String(path).replace(/^\/+/, '')}`;

const Badge = ({ color = 'yellow', children }) => {
  const map = {
    yellow: 'bg-yellow-900/40 text-yellow-300 border-yellow-700',
    green:  'bg-green-900/40 text-green-300 border-green-700',
    red:    'bg-red-900/40 text-red-300 border-red-700',
    gray:   'bg-gray-800 text-gray-300 border-gray-700',
    blue:   'bg-blue-900/40 text-blue-300 border-blue-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] ${map[color] || map.yellow}`}>
      {children}
    </span>
  );
};

export default function AdminClients() {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | pending | approved | active
  const [autoRefresh, setAutoRefresh] = useState(true);

  const token = useMemo(() => localStorage.getItem('token') || '', []);
  const timerRef = useRef(null);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const url = joinUrl(API_BASE_URL, '/admin/oauth/clients');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Failed to load clients');
      setClients(Array.isArray(data.clients) ? data.clients : []);
    } catch (e) {
      setError(e.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }
    fetchClients();
  }, [token, fetchClients]);

  // Auto-refresh every 15s
  useEffect(() => {
    if (!autoRefresh) {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    timerRef.current = setInterval(fetchClients, 15000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoRefresh, fetchClients]);

  const filtered = useMemo(() => {
    let list = clients;
    if (filter === 'pending')   list = list.filter(c => !!c.pending && !c.approved);
    if (filter === 'approved')  list = list.filter(c => !!c.approved);
    if (filter === 'active')    list = list.filter(c => !!c.active);

    const term = q.trim().toLowerCase();
    if (!term) return list;
    return list.filter((c) =>
      [c.clientId, c.name, c.homepageUrl]
        .filter(Boolean)
        .some((x) => String(x).toLowerCase().includes(term))
    );
  }, [clients, q, filter]);

  async function doAction(url, body = {}) {
    setError('');
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.message || 'Request failed');
    await fetchClients();
  }

  const base = API_BASE_URL;
  const approve    = (id) => doAction(joinUrl(base, `/admin/oauth/clients/${encodeURIComponent(id)}/approve`));
  const reject     = (id) => doAction(joinUrl(base, `/admin/oauth/clients/${encodeURIComponent(id)}/reject`));
  const activate   = (id) => doAction(joinUrl(base, `/admin/oauth/clients/${encodeURIComponent(id)}/activate`));
  const deactivate = (id) => doAction(joinUrl(base, `/admin/oauth/clients/${encodeURIComponent(id)}/deactivate`));

  const count = {
    total: clients.length,
    pending: clients.filter(c => !!c.pending && !c.approved).length,
    approved: clients.filter(c => !!c.approved).length,
    active: clients.filter(c => !!c.active).length,
  };

  return (
    <Layout>
      <div className="space-y-8">
        <section className="rounded-[2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.2),transparent_34%),linear-gradient(180deg,#111111,#030303)] p-6 text-white shadow-xl sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.24em] text-amber-200">OAuth Clients</p>
              <h1 className="mt-3 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
                Approve, activate, and monitor connected apps.
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-[#cfcfcf]">
                This panel now sits inside the shared Taban-style admin shell, with a responsive layout and matching rounded navigation surfaces.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 rounded-full border border-[#f5b221]/20 bg-black px-4 py-2 text-sm text-[#f7d88a]">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="h-4 w-4"
                />
                Auto refresh
              </label>
              <button
                onClick={fetchClients}
                className="rounded-full bg-amber-300 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:brightness-105"
              >
                Refresh now
              </button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <div className="rounded-[1.6rem] border border-[#f5b221]/14 bg-[linear-gradient(180deg,#111111,#040404)] p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8f8f8f]">Total</div>
            <div className="mt-3 text-3xl font-black text-[#f5b221]">{count.total}</div>
          </div>
          <div className="rounded-[1.6rem] border border-[#f5b221]/14 bg-[linear-gradient(180deg,#111111,#040404)] p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8f8f8f]">Pending</div>
            <div className="mt-3 text-3xl font-black text-[#f5b221]">{count.pending}</div>
          </div>
          <div className="rounded-[1.6rem] border border-[#f5b221]/14 bg-[linear-gradient(180deg,#111111,#040404)] p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8f8f8f]">Approved</div>
            <div className="mt-3 text-3xl font-black text-[#f5b221]">{count.approved}</div>
          </div>
          <div className="rounded-[1.6rem] border border-[#f5b221]/14 bg-[linear-gradient(180deg,#111111,#040404)] p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8f8f8f]">Active</div>
            <div className="mt-3 text-3xl font-black text-[#f5b221]">{count.active}</div>
          </div>
        </section>

        <section className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-5 shadow-sm sm:p-6">
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${filter==='all' ? 'border-[#f5b221] bg-[#f5b221] text-black' : 'border-[#333333] bg-[#111111] text-[#d3d3d3] hover:bg-[#191919]'}`}
                onClick={() => setFilter('all')}
              >All</button>
              <button
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${filter==='pending' ? 'border-[#f5b221] bg-[#f5b221] text-black' : 'border-[#333333] bg-[#111111] text-[#d3d3d3] hover:bg-[#191919]'}`}
                onClick={() => setFilter('pending')}
              >Pending</button>
              <button
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${filter==='approved' ? 'border-[#f5b221] bg-[#f5b221] text-black' : 'border-[#333333] bg-[#111111] text-[#d3d3d3] hover:bg-[#191919]'}`}
                onClick={() => setFilter('approved')}
              >Approved</button>
              <button
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${filter==='active' ? 'border-[#f5b221] bg-[#f5b221] text-black' : 'border-[#333333] bg-[#111111] text-[#d3d3d3] hover:bg-[#191919]'}`}
                onClick={() => setFilter('active')}
              >Active</button>
            </div>

            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, clientId, homepage..."
              className="w-full rounded-full border border-[#333333] bg-black px-5 py-3 text-sm text-white outline-none transition focus:border-[#f5b221] lg:max-w-md"
            />
          </div>

          {error && (
            <div className="mb-4 rounded-[1.4rem] border border-red-500/25 bg-red-950/15 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          )}

          {loading ? (
            <div className="rounded-[1.5rem] border border-dashed border-[#2b2b2b] px-5 py-10 text-center text-[#989898]">
              Loading clients...
            </div>
          ) : (
            <div className="overflow-hidden rounded-[1.6rem] border border-[#1f1f1f]">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-[#111111]">
                    <tr>
                      <th className="p-4 text-left font-semibold text-[#f7d88a]">Client</th>
                      <th className="p-4 text-left font-semibold text-[#f7d88a]">Redirect URIs</th>
                      <th className="p-4 text-left font-semibold text-[#f7d88a]">Scopes</th>
                      <th className="p-4 text-left font-semibold text-[#f7d88a]">Status</th>
                      <th className="p-4 text-left font-semibold text-[#f7d88a]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c) => (
                      <tr key={c.clientId} className="border-t border-[#1f1f1f] bg-black/50 align-top">
                        <td className="p-4">
                          <div className="font-semibold text-white">{c.name || c.clientId}</div>
                          <div className="mt-1 break-all text-xs text-[#8f8f8f]">{c.clientId}</div>
                          {c.homepageUrl && (
                            <a
                              className="mt-2 inline-block break-all text-xs font-semibold text-[#f7d88a] hover:underline"
                              href={c.homepageUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {c.homepageUrl}
                            </a>
                          )}
                        </td>

                        <td className="p-4">
                          <ul className="space-y-2 text-xs text-[#b7b7b7]">
                            {(c.redirectUris || []).map((u) => (
                              <li key={u} className="break-all">{u}</li>
                            ))}
                            {!c.redirectUris?.length && <li className="text-[#777777]">—</li>}
                          </ul>
                        </td>

                        <td className="p-4">
                          <div className="text-xs text-[#b7b7b7]">
                            {(c.allowedScopes || []).join(' ') || '—'}
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="flex flex-col gap-2">
                            {c.pending != null && (
                              <Badge color={c.pending ? 'yellow' : 'gray'}>{c.pending ? 'Pending' : 'Not Pending'}</Badge>
                            )}
                            <Badge color={c.approved ? 'green' : 'yellow'}>
                              {c.approved ? 'Approved' : 'Not Approved'}
                            </Badge>
                            <Badge color={c.active ? 'green' : 'red'}>
                              {c.active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge color={c.requireVerified ? 'blue' : 'gray'}>
                              {c.requireVerified ? 'Require Verified Users' : 'Allow Unverified Users'}
                            </Badge>
                          </div>
                        </td>

                        <td className="p-4">
                          <div className="flex flex-wrap gap-2">
                            {!c.approved && (
                              <button
                                onClick={() => approve(c.clientId).catch((e) => setError(e.message))}
                                className="rounded-full bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-500"
                              >
                                Approve
                              </button>
                            )}
                            {c.approved && (
                              <button
                                onClick={() => reject(c.clientId).catch((e) => setError(e.message))}
                                className="rounded-full bg-amber-500 px-3 py-2 text-xs font-semibold text-slate-950 transition hover:bg-amber-400"
                              >
                                Revoke Approval
                              </button>
                            )}
                            {!c.active ? (
                              <button
                                onClick={() => activate(c.clientId).catch((e) => setError(e.message))}
                                className="rounded-full bg-amber-300 px-3 py-2 text-xs font-semibold text-slate-950 transition hover:brightness-105"
                              >
                                Activate
                              </button>
                            ) : (
                              <button
                                onClick={() => deactivate(c.clientId).catch((e) => setError(e.message))}
                                className="rounded-full bg-rose-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-rose-500"
                              >
                                Deactivate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}

                    {!filtered.length ? (
                      <tr>
                          <td className="p-8 text-center text-[#989898]" colSpan={5}>
                          No clients found.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
